/**
 * TechUI — Shared tech-style UI utilities for consistent button/panel rendering.
 * Provides chamfered rectangle drawing and pre-styled button factories.
 *
 * Usage:
 *   import { TechUI, TECH } from '@/ui/TechUI';
 *   TechUI.createButton(scene, x, y, 'LABEL', { onClick: () => ... });
 */

import Phaser from 'phaser';
import { t } from '@/i18n/I18n';
import { SoundManager } from '@/systems/SoundManager';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';

// ── Tech color palette ──
export const TECH = {
  COLOR:   0xcccccc,   // default stroke
  HOVER:   0xffffff,   // hover stroke
  ACCENT:  0xffcc00,   // accent (active items)
  DANGER:  0xff4444,   // danger actions
  SUCCESS: 0x44ff88,   // success/confirm
  DIM:     0x666688,   // dimmed/disabled
  BG:      0x0d0d1a,   // panel background
  CHAMFER: 8,          // corner cut size in px
} as const;

/** Draw a chamfered rect outline (bottom-right corner clipped at 45°). */
export function drawTechRect(
  gfx: Phaser.GameObjects.Graphics,
  x: number, y: number, w: number, h: number,
  color: number, alpha = 1, lineW = 1.5,
): void {
  const c = TECH.CHAMFER;
  gfx.lineStyle(lineW, color, alpha);
  gfx.beginPath();
  gfx.moveTo(x, y);
  gfx.lineTo(x + w, y);
  gfx.lineTo(x + w, y + h - c);
  gfx.lineTo(x + w - c, y + h);
  gfx.lineTo(x, y + h);
  gfx.closePath();
  gfx.strokePath();
}

/** Fill a chamfered rect (for panel backgrounds). */
export function fillTechRect(
  gfx: Phaser.GameObjects.Graphics,
  x: number, y: number, w: number, h: number,
  color: number, alpha = 0.9,
): void {
  const c = TECH.CHAMFER;
  gfx.fillStyle(color, alpha);
  gfx.beginPath();
  gfx.moveTo(x, y);
  gfx.lineTo(x + w, y);
  gfx.lineTo(x + w, y + h - c);
  gfx.lineTo(x + w - c, y + h);
  gfx.lineTo(x, y + h);
  gfx.closePath();
  gfx.fillPath();
}

// ── Button options ──
export interface TechButtonOptions {
  onClick: () => void;
  color?: number;        // stroke/text color (default TECH.COLOR)
  width?: number;        // explicit width (auto-sized from text if omitted)
  height?: number;       // explicit height (default 36)
  fontSize?: string;     // default '12px'
  fontStyle?: string;    // default 'bold'
  depth?: number;        // default 2
  icon?: string;         // optional icon prefix (e.g., '🔧')
  description?: string;  // optional subtitle below label
}

/** Hex number to CSS color string. */
function hexStr(c: number): string {
  return '#' + c.toString(16).padStart(6, '0');
}

/**
 * Create a tech-styled button: chamfered outline, hover glow, click feedback.
 * Returns a Container so it can be added to panels or positioned freely.
 */
export function createTechButton(
  scene: Phaser.Scene,
  x: number, y: number,
  label: string,
  opts: TechButtonOptions,
): Phaser.GameObjects.Container {
  const color = opts.color ?? TECH.COLOR;
  const fontSize = opts.fontSize ?? '14px';
  const fontStyle = opts.fontStyle ?? 'bold';
  const depth = opts.depth ?? 2;

  const container = scene.add.container(x, y).setDepth(depth);

  // Measure text to auto-size
  const textObj = scene.add.text(0, 0, label, {
    fontSize, fontFamily: 'monospace', fontStyle,
  }).setOrigin(0.5);

  const padX = 18;
  const padY = 10;
  const btnW = opts.width ?? (textObj.width + padX * 2);
  const btnH = opts.height ?? Math.max(40, textObj.height + padY * 2);

  // Optional description (subtitle)
  let descObj: Phaser.GameObjects.Text | null = null;
  let totalH = btnH;
  if (opts.description) {
    descObj = scene.add.text(0, 12, opts.description, {
      fontSize: '11px', color: hexStr(TECH.DIM), fontFamily: 'monospace',
    }).setOrigin(0.5);
    totalH = Math.max(btnH, textObj.height + descObj.height + padY * 2 + 6);
    textObj.setY(-8);
    descObj.setY(10);
  }

  // Optional icon
  if (opts.icon) {
    const iconText = scene.add.text(-textObj.width / 2 - 14, textObj.y, opts.icon, {
      fontSize, fontFamily: 'monospace',
    }).setOrigin(0.5);
    container.add(iconText);
  }

  const finalW = opts.width ?? btnW;
  const finalH = opts.height ?? totalH;

  // Graphics for frame
  const gfx = scene.add.graphics();
  const drawFrame = (c: number, a: number, lw: number) => {
    gfx.clear();
    drawTechRect(gfx, -finalW / 2, -finalH / 2, finalW, finalH, c, a, lw);
  };
  drawFrame(color, 0.7, 1);

  // Hit area
  const hitArea = scene.add.rectangle(0, 0, finalW, finalH, 0x000000, 0)
    .setInteractive({ useHandCursor: true });

  container.add(gfx);
  container.add(textObj);
  if (descObj) container.add(descObj);
  container.add(hitArea);

  // Set text color
  textObj.setColor(hexStr(color));

  // Hover effects
  hitArea.on('pointerover', () => {
    drawFrame(color, 1, 1.5);
    textObj.setColor('#ffffff');
    if (descObj) descObj.setColor(hexStr(color));
    scene.tweens.add({
      targets: container,
      scaleX: 1.05, scaleY: 1.05,
      duration: 120,
      ease: 'Sine.easeOut',
    });
  });
  hitArea.on('pointerout', () => {
    drawFrame(color, 0.7, 1);
    textObj.setColor(hexStr(color));
    if (descObj) descObj.setColor(hexStr(TECH.DIM));
    scene.tweens.add({
      targets: container,
      scaleX: 1, scaleY: 1,
      duration: 120,
      ease: 'Sine.easeOut',
    });
  });
  hitArea.on('pointerdown', opts.onClick);

  return container;
}

/**
 * Create a small inline tech button (for toolbars, panels).
 * Smaller padding, thinner frame.
 */
export function createSmallTechButton(
  scene: Phaser.Scene,
  x: number, y: number,
  label: string,
  color: number,
  onClick: () => void,
  depth = 2,
): Phaser.GameObjects.Container {
  return createTechButton(scene, x, y, label, {
    onClick,
    color,
    fontSize: '12px',
    height: 32,
    depth,
  });
}

/**
 * Add subtle scale hover effect to any interactive game object or container.
 * Attach to existing pointerover/pointerout without replacing them.
 */
export function addHoverScale(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.GameObject & { setScale?: (x: number, y?: number) => void },
  hitArea?: Phaser.GameObjects.GameObject,
  scale = 1.05,
): void {
  const obj = hitArea ?? target;
  obj.on('pointerover', () => {
    scene.tweens.add({ targets: target, scaleX: scale, scaleY: scale, duration: 120, ease: 'Sine.easeOut' });
  });
  obj.on('pointerout', () => {
    scene.tweens.add({ targets: target, scaleX: 1, scaleY: 1, duration: 120, ease: 'Sine.easeOut' });
  });
}

/**
 * Draw a beveled metallic plate on a Graphics object.
 * Simulates a 3D metal surface with light coming from top-left.
 */
export function drawMetalPlate(
  gfx: Phaser.GameObjects.Graphics,
  x: number, y: number, w: number, h: number,
  baseColor: number = 0x556677,
  opts?: { bevel?: number; alpha?: number },
): void {
  const bevel = opts?.bevel ?? 2;
  const alpha = opts?.alpha ?? 1;

  // Extract RGB components for color manipulation
  const r = (baseColor >> 16) & 0xff;
  const g = (baseColor >> 8) & 0xff;
  const b = baseColor & 0xff;

  // Color variants
  const lighter = ((Math.min(255, r + 50) << 16) | (Math.min(255, g + 50) << 8) | Math.min(255, b + 50));
  const darker  = ((Math.max(0, r - 40) << 16) | (Math.max(0, g - 40) << 8) | Math.max(0, b - 40));
  const darkest = ((Math.max(0, r - 70) << 16) | (Math.max(0, g - 70) << 8) | Math.max(0, b - 70));

  // Outer shadow (bottom-right)
  gfx.fillStyle(0x000000, 0.3 * alpha);
  gfx.fillRect(x + 2, y + 2, w, h);

  // Bottom/right bevel (dark edge)
  gfx.fillStyle(darkest, 0.9 * alpha);
  gfx.fillRect(x, y, w, h);

  // Top/left bevel (light edge)
  gfx.fillStyle(lighter, 0.8 * alpha);
  gfx.fillRect(x, y, w - bevel, h - bevel);

  // Main surface
  gfx.fillStyle(baseColor, 0.95 * alpha);
  gfx.fillRect(x + bevel, y + bevel, w - bevel * 2, h - bevel * 2);

  // Top highlight gradient strip
  gfx.fillStyle(0xffffff, 0.08 * alpha);
  gfx.fillRect(x + bevel, y + bevel, w - bevel * 2, Math.max(2, h * 0.25));

  // Bottom shadow gradient strip
  gfx.fillStyle(0x000000, 0.1 * alpha);
  gfx.fillRect(x + bevel, y + h - bevel - Math.max(2, h * 0.2), w - bevel * 2, Math.max(2, h * 0.2));

  // Subtle horizontal brushed-metal lines
  gfx.lineStyle(1, 0xffffff, 0.04 * alpha);
  const lineStep = 4;
  for (let ly = y + bevel + 3; ly < y + h - bevel - 2; ly += lineStep) {
    gfx.lineBetween(x + bevel + 1, ly, x + w - bevel - 1, ly);
  }

  // Corner rivets
  const rivetR = Math.max(1.5, Math.min(3, h * 0.06));
  const pad = bevel + rivetR + 2;
  const rivets = [
    [x + pad, y + pad],
    [x + w - pad, y + pad],
    [x + pad, y + h - pad],
    [x + w - pad, y + h - pad],
  ];
  for (const [rx, ry] of rivets) {
    gfx.fillStyle(darker, 0.9 * alpha);
    gfx.fillCircle(rx, ry, rivetR);
    gfx.fillStyle(lighter, 0.5 * alpha);
    gfx.fillCircle(rx - 0.5, ry - 0.5, rivetR * 0.5);
  }
}

/**
 * Create engraved text — dark text with a subtle light offset below for inset look.
 */
export function createEngravedText(
  scene: Phaser.Scene,
  x: number, y: number,
  text: string,
  style?: { fontSize?: string; color?: string; fontStyle?: string },
): Phaser.GameObjects.Container {
  const fontSize = style?.fontSize ?? '10px';
  const color = style?.color ?? '#222233';
  const fontStyle = style?.fontStyle ?? 'bold';

  const container = scene.add.container(x, y);

  // Light offset (highlight below the engraving)
  const highlight = scene.add.text(0, 1, text, {
    fontSize, color: '#ffffff', fontFamily: 'monospace', fontStyle,
  }).setOrigin(0.5).setAlpha(0.15);
  container.add(highlight);

  // Main dark text (the "groove")
  const main = scene.add.text(0, 0, text, {
    fontSize, color, fontFamily: 'monospace', fontStyle,
  }).setOrigin(0.5);
  container.add(main);

  return container;
}

/**
 * Add a periodic diagonal shine sweep to a tech button container.
 * A bright diagonal bar slides across the button every `intervalMs`.
 */
export function addShineEffect(
  scene: Phaser.Scene,
  btn: Phaser.GameObjects.Container,
  btnW: number,
  btnH: number,
  intervalMs: number = 4000,
  initialDelayMs: number = 500,
): () => void {
  // Create shine bar (diagonal white gradient band)
  const shineW = 16;
  const shineH = btnH + 20; // taller than button to cover diagonal
  const shine = scene.add.graphics();
  // Draw a skewed bright bar
  shine.fillStyle(0xffffff, 0.25);
  shine.beginPath();
  shine.moveTo(-shineW / 2, -shineH / 2);
  shine.lineTo(shineW / 2, -shineH / 2);
  shine.lineTo(-shineW / 2 + 10, shineH / 2);
  shine.lineTo(-shineW / 2 - shineW + 10, shineH / 2);
  shine.closePath();
  shine.fillPath();
  // Inner bright core
  shine.fillStyle(0xffffff, 0.15);
  shine.beginPath();
  shine.moveTo(-4, -shineH / 2);
  shine.lineTo(4, -shineH / 2);
  shine.lineTo(-4 + 10, shineH / 2);
  shine.lineTo(-4 - 8 + 10, shineH / 2);
  shine.closePath();
  shine.fillPath();

  shine.setAlpha(0);
  btn.add(shine);

  // Mask: only visible within button bounds
  const mask = scene.make.graphics({ x: 0, y: 0 });
  mask.fillRect(btn.x - btnW / 2, btn.y - btnH / 2, btnW, btnH);
  shine.setMask(mask.createGeometryMask());

  // Periodic sweep tween
  const startX = -btnW / 2 - shineW;
  const endX = btnW / 2 + shineW;

  const doSweep = () => {
    // Refresh mask to current container position (handles moved cards)
    mask.clear();
    mask.fillRect(btn.x - btnW / 2, btn.y - btnH / 2, btnW, btnH);

    shine.setPosition(startX, 0);
    shine.setAlpha(1);
    scene.tweens.add({
      targets: shine,
      x: endX,
      duration: 600,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        shine.setAlpha(0);
      },
    });
  };

  // First sweep after initial delay, then repeat
  scene.time.delayedCall(initialDelayMs, () => {
    doSweep();
    scene.time.addEvent({ delay: intervalMs, callback: doSweep, loop: true });
  });

  return doSweep;
}

// ── Tilt 3D options ──
export interface Tilt3DOptions {
  hoverScale?: number;      // uniform hover scale (default 1.06)
  tiltStrength?: number;    // rotation amplitude in radians (default 0.06)
  shiftPx?: number;         // max position offset toward mouse (default 3)
  camera?: Phaser.Cameras.Scene2D.Camera; // for multi-camera setups
  shadowAlpha?: number;     // shadow max alpha (default 0.3)
}

/**
 * Add a fake-3D depth tilt to a container.
 * Combines rotation + position shift + dynamic shadow for depth illusion.
 * No flat scale asymmetry (doesn't work on containers).
 */
export function addTilt3DEffect(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  hitArea: Phaser.GameObjects.Rectangle,
  w: number, h: number,
  opts?: Tilt3DOptions,
): void {
  const hoverScale = opts?.hoverScale ?? 1.06;
  const tilt = opts?.tiltStrength ?? 0.06;
  const shiftPx = opts?.shiftPx ?? 3;
  const cam = opts?.camera ?? scene.cameras.main;
  const shadowAlpha = opts?.shadowAlpha ?? 0.3;

  // Store base position (set once the idle float tween settles)
  let baseX = container.x;
  let baseY = container.y;

  // Shadow behind the plate
  const shadow = scene.add.graphics();
  shadow.fillStyle(0x000000, shadowAlpha);
  shadow.fillRoundedRect(-w / 2 + 2, -h / 2 + 2, w, h, 3);
  shadow.setAlpha(0);
  container.addAt(shadow, 0);

  let isHovering = false;

  hitArea.on('pointerover', () => {
    isHovering = true;
    // Snapshot current position as base (accounts for idle float tween)
    baseX = container.x;
    baseY = container.y;
    shadow.setAlpha(1);
    container.setScale(hoverScale);
  });

  hitArea.on('pointermove', (pointer: Phaser.Input.Pointer) => {
    if (!isHovering) return;

    const worldPoint = cam.getWorldPoint(pointer.x, pointer.y);
    const localX = worldPoint.x - container.x;
    const localY = worldPoint.y - container.y;

    const nx = Phaser.Math.Clamp(localX / (w / 2), -1, 1);
    const ny = Phaser.Math.Clamp(localY / (h / 2), -1, 1);

    // Y-axis depth rotation (horizontal mouse → plate turns left/right)
    container.setRotation(nx * tilt);

    // X-axis depth: vertical foreshortening (mouse up → top recedes, plate compresses)
    const sy = hoverScale - ny * tilt * 0.6;
    container.setScale(hoverScale, sy);

    // Position shift: plate slides slightly toward the mouse (parallax depth)
    container.setPosition(baseX + nx * shiftPx, baseY + ny * shiftPx * 0.6);

    // Shadow shifts opposite to tilt direction
    shadow.setPosition(-nx * 5, -ny * 4 + 3);
    shadow.setAlpha(shadowAlpha * (0.4 + 0.6 * Math.sqrt(nx * nx + ny * ny)));
  });

  hitArea.on('pointerout', () => {
    isHovering = false;
    scene.tweens.add({
      targets: container,
      scaleX: 1, scaleY: 1, rotation: 0,
      x: baseX, y: baseY,
      duration: 250,
      ease: 'Sine.easeOut',
    });
    scene.tweens.add({
      targets: shadow,
      x: 0, y: 3,
      alpha: 0,
      duration: 250,
      ease: 'Sine.easeOut',
    });
  });
}

// ── Reusable slider widget ──

/** Create a labeled draggable slider. Returns a Container with a `refresh(v)` method to update externally. */
export function createTechSlider(
  scene: Phaser.Scene,
  x: number, y: number,
  label: string,
  min: number, max: number, step: number,
  initial: number,
  onChange: (value: number) => void,
  trackW = 150,
): Phaser.GameObjects.Container & { refresh: (v: number) => void } {
  const container = scene.add.container(x, y) as Phaser.GameObjects.Container & { refresh: (v: number) => void };

  const labelText = scene.add.text(0, 0, label, {
    fontSize: '11px', color: '#aaaacc', fontFamily: 'monospace',
  }).setOrigin(0, 0.5);
  container.add(labelText);

  const trackX = 90;
  const trackGfx = scene.add.graphics();
  trackGfx.lineStyle(2, 0x444466, 1);
  trackGfx.lineBetween(trackX, 0, trackX + trackW, 0);
  container.add(trackGfx);

  const valText = scene.add.text(trackX + trackW + 8, 0, '', {
    fontSize: '11px', color: '#ffcc00', fontFamily: 'monospace',
  }).setOrigin(0, 0.5);
  container.add(valText);

  const knobGfx = scene.add.graphics();
  container.add(knobGfx);

  const knob = scene.add.rectangle(0, 0, 14, 18, 0x000000, 0)
    .setInteractive({ useHandCursor: true, draggable: true });
  container.add(knob);

  // Track click area
  const trackHit = scene.add.rectangle(trackX + trackW / 2, 0, trackW, 16, 0x000000, 0)
    .setInteractive({ useHandCursor: true });
  container.add(trackHit);

  let currentValue = initial;

  const applySlider = (rawX: number) => {
    const clampedX = Phaser.Math.Clamp(rawX, trackX, trackX + trackW);
    const normalized = (clampedX - trackX) / trackW;
    const raw = min + normalized * (max - min);
    const snapped = Math.round(raw / step) * step;
    const val = Phaser.Math.Clamp(snapped, min, max);
    currentValue = val;
    drawKnob(val);
    onChange(val);
  };

  const drawKnob = (val: number) => {
    const norm = (val - min) / (max - min);
    const kx = trackX + norm * trackW;
    knobGfx.clear();
    knobGfx.fillStyle(0x66ccff, 0.9);
    knobGfx.fillCircle(kx, 0, 5);
    knob.setPosition(kx, 0);
    valText.setText(step >= 1 ? `${val}` : val.toFixed(1));
  };

  /** Convert world X to local X relative to this container (handles nested parents) */
  const worldToLocalX = (worldX: number): number => {
    let offsetX = 0;
    let node: Phaser.GameObjects.Container | null = container;
    while (node) {
      offsetX += node.x;
      node = (node.parentContainer ?? null) as Phaser.GameObjects.Container | null;
    }
    return worldX - offsetX;
  };

  knob.on('drag', (_p: unknown, dragX: number) => applySlider(worldToLocalX(dragX)));
  trackHit.on('pointerdown', (p: Phaser.Input.Pointer) => applySlider(worldToLocalX(p.worldX)));

  container.refresh = (v: number) => {
    currentValue = v;
    drawKnob(v);
  };

  drawKnob(initial);
  return container;
}

// ══════════════════════════════════════════
//  CONFIRMATION DIALOG
// ══════════════════════════════════════════

/**
 * Show a modal confirmation dialog. Returns a container that auto-destroys
 * when the user clicks Confirm or Cancel.
 */
export function showConfirmDialog(
  scene: Phaser.Scene,
  message: string,
  onConfirm: () => void,
  opts?: { confirmLabel?: string; cancelLabel?: string; confirmColor?: number },
): Phaser.GameObjects.Container {
  const confirmLabel = opts?.confirmLabel ?? t('dialog.confirm');
  const cancelLabel = opts?.cancelLabel ?? t('dialog.cancel');
  const confirmColor = opts?.confirmColor ?? TECH.DANGER;

  const container = scene.add.container(0, 0).setDepth(200);

  // Fullscreen dim overlay
  const overlay = scene.add.rectangle(
    CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2,
    CANVAS_WIDTH, CANVAS_HEIGHT,
    0x000000, 0.6,
  ).setInteractive(); // blocks clicks behind
  container.add(overlay);

  // Dialog box
  const boxW = 300;
  const boxH = 120;
  const cx = CANVAS_WIDTH / 2;
  const cy = CANVAS_HEIGHT / 2;

  const bg = scene.add.rectangle(cx, cy, boxW, boxH, 0x0a0a1e, 0.97)
    .setStrokeStyle(1.5, TECH.COLOR, 0.6);
  container.add(bg);

  // Message
  const msgText = scene.add.text(cx, cy - 22, message, {
    fontSize: '12px', color: hexStr(TECH.COLOR), fontFamily: 'monospace',
    wordWrap: { width: boxW - 30 }, align: 'center',
  }).setOrigin(0.5);
  container.add(msgText);

  const dismiss = () => { container.destroy(); };

  // Confirm button
  const confirmBtn = createTechButton(scene, cx - 60, cy + 28, confirmLabel, {
    onClick: () => { SoundManager.play('clickConfirm'); dismiss(); onConfirm(); },
    width: 100, height: 28, fontSize: '11px', color: confirmColor, depth: 201,
  });
  container.add(confirmBtn);

  // Cancel button
  const cancelBtn = createTechButton(scene, cx + 60, cy + 28, cancelLabel, {
    onClick: () => { SoundManager.play('clickCancel'); dismiss(); },
    width: 100, height: 28, fontSize: '11px', color: TECH.DIM, depth: 201,
  });
  container.add(cancelBtn);

  return container;
}

// ══════════════════════════════════════════
//  TEXT INPUT DIALOG
// ══════════════════════════════════════════

/**
 * Show a modal in-game text input dialog (replaces browser prompt()).
 * Uses an HTML <input> overlaid on the Phaser canvas, styled to match the tech UI.
 * Auto-destroys on confirm, cancel, or click-outside.
 */
export function showTextInputDialog(
  scene: Phaser.Scene,
  title: string,
  initialValue: string,
  onConfirm: (value: string) => void,
  opts?: { maxLength?: number; placeholder?: string; confirmLabel?: string; cancelLabel?: string },
): Phaser.GameObjects.Container {
  const maxLen = opts?.maxLength ?? 30;
  const confirmLabel = opts?.confirmLabel ?? t('dialog.ok');
  const cancelLabel = opts?.cancelLabel ?? t('dialog.cancel');

  const container = scene.add.container(0, 0).setDepth(200);

  // Fullscreen dim overlay
  const overlay = scene.add.rectangle(
    CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2,
    CANVAS_WIDTH, CANVAS_HEIGHT,
    0x000000, 0.6,
  ).setInteractive();
  container.add(overlay);

  // Dialog box
  const boxW = 320;
  const boxH = 130;
  const cx = CANVAS_WIDTH / 2;
  const cy = CANVAS_HEIGHT / 2;

  const bg = scene.add.rectangle(cx, cy, boxW, boxH, 0x0a0a1e, 0.97)
    .setStrokeStyle(1.5, TECH.COLOR, 0.6);
  container.add(bg);

  // Title
  container.add(scene.add.text(cx, cy - 40, title, {
    fontSize: '13px', color: hexStr(TECH.COLOR), fontFamily: 'monospace', fontStyle: 'bold',
  }).setOrigin(0.5));

  // ── HTML input element overlaid on canvas ──
  const canvas = scene.game.canvas;
  const canvasRect = canvas.getBoundingClientRect();
  // Convert game coords to page coords
  const scaleX = canvasRect.width / CANVAS_WIDTH;
  const scaleY = canvasRect.height / CANVAS_HEIGHT;
  const inputW = 240;
  const inputH = 24;
  const inputPageX = canvasRect.left + (cx - inputW / 2) * scaleX;
  const inputPageY = canvasRect.top + (cy - 12) * scaleY;

  const input = document.createElement('input');
  input.type = 'text';
  input.value = initialValue;
  input.maxLength = maxLen;
  if (opts?.placeholder) input.placeholder = opts.placeholder;
  input.style.cssText = `
    position: fixed;
    left: ${inputPageX}px;
    top: ${inputPageY}px;
    width: ${inputW * scaleX}px;
    height: ${inputH * scaleY}px;
    font-family: monospace;
    font-size: ${12 * scaleY}px;
    color: #ffcc00;
    background: #111133;
    border: 1px solid #4466aa;
    outline: none;
    padding: 2px 6px;
    box-sizing: border-box;
    z-index: 9999;
  `;
  document.body.appendChild(input);

  // Placeholder rectangle behind the input (visual alignment guide)
  const inputBg = scene.add.rectangle(cx, cy - 12 + inputH / 2, inputW, inputH, 0x111133, 0.8)
    .setStrokeStyle(1, 0x4466aa, 0.6);
  container.add(inputBg);

  // Disable Phaser keyboard while input is focused
  const phaserKb = scene.input.keyboard;
  if (phaserKb) phaserKb.enabled = false;

  const dismiss = () => {
    input.remove();
    if (phaserKb) phaserKb.enabled = true;
    container.destroy();
  };

  // Confirm action
  const doConfirm = () => {
    const val = input.value.trim();
    if (val) {
      SoundManager.play('clickConfirm');
      dismiss();
      onConfirm(val);
    }
  };

  // Enter to confirm, Escape to cancel
  input.addEventListener('keydown', (e: KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') doConfirm();
    if (e.key === 'Escape') { SoundManager.play('clickCancel'); dismiss(); }
  });

  // Confirm button
  const confirmBtn = createTechButton(scene, cx - 55, cy + 32, confirmLabel, {
    onClick: doConfirm,
    width: 90, height: 26, fontSize: '11px', color: TECH.SUCCESS, depth: 201,
  });
  container.add(confirmBtn);

  // Cancel button
  const cancelBtn = createTechButton(scene, cx + 55, cy + 32, cancelLabel, {
    onClick: () => { SoundManager.play('clickCancel'); dismiss(); },
    width: 90, height: 26, fontSize: '11px', color: TECH.DIM, depth: 201,
  });
  container.add(cancelBtn);

  // Click overlay to cancel
  overlay.on('pointerdown', () => { SoundManager.play('clickCancel'); dismiss(); });

  // Focus input on next frame
  scene.time.delayedCall(50, () => {
    input.focus();
    input.select();
  });

  return container;
}

/**
 * Multiline text area dialog — for editing longer text like dialogue lines.
 * Ctrl+Enter to confirm, Escape to cancel, click-outside to cancel.
 */
export function showTextAreaDialog(
  scene: Phaser.Scene,
  title: string,
  initialValue: string,
  onConfirm: (value: string) => void,
  opts?: { maxLength?: number; placeholder?: string; rows?: number },
): Phaser.GameObjects.Container {
  const maxLen = opts?.maxLength ?? 500;
  const rows = opts?.rows ?? 5;
  const confirmLabel = t('dialog.ok');
  const cancelLabel = t('dialog.cancel');

  const container = scene.add.container(0, 0).setDepth(200);

  // Fullscreen dim overlay
  const overlay = scene.add.rectangle(
    CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2,
    CANVAS_WIDTH, CANVAS_HEIGHT,
    0x000000, 0.6,
  ).setInteractive();
  container.add(overlay);

  // Dialog box
  const boxW = 400;
  const boxH = 220;
  const cx = CANVAS_WIDTH / 2;
  const cy = CANVAS_HEIGHT / 2;

  const bg = scene.add.rectangle(cx, cy, boxW, boxH, 0x0a0a1e, 0.97)
    .setStrokeStyle(1.5, TECH.COLOR, 0.6);
  container.add(bg);

  // Title
  container.add(scene.add.text(cx, cy - boxH / 2 + 18, title, {
    fontSize: '13px', color: hexStr(TECH.COLOR), fontFamily: 'monospace', fontStyle: 'bold',
  }).setOrigin(0.5));

  // Hint text
  container.add(scene.add.text(cx, cy + boxH / 2 - 50, 'Ctrl+Enter to confirm', {
    fontSize: '9px', color: '#555577', fontFamily: 'monospace',
  }).setOrigin(0.5));

  // ── HTML textarea element overlaid on canvas ──
  const canvas = scene.game.canvas;
  const canvasRect = canvas.getBoundingClientRect();
  const scaleX = canvasRect.width / CANVAS_WIDTH;
  const scaleY = canvasRect.height / CANVAS_HEIGHT;
  const taW = 360;
  const taH = rows * 22;
  const taPageX = canvasRect.left + (cx - taW / 2) * scaleX;
  const taPageY = canvasRect.top + (cy - boxH / 2 + 38) * scaleY;

  const textarea = document.createElement('textarea');
  textarea.value = initialValue;
  textarea.maxLength = maxLen;
  textarea.rows = rows;
  if (opts?.placeholder) textarea.placeholder = opts.placeholder;
  textarea.style.cssText = `
    position: fixed;
    left: ${taPageX}px;
    top: ${taPageY}px;
    width: ${taW * scaleX}px;
    height: ${taH * scaleY}px;
    font-family: monospace;
    font-size: ${12 * scaleY}px;
    color: #ffcc00;
    background: #111133;
    border: 1px solid #4466aa;
    outline: none;
    padding: 6px 8px;
    box-sizing: border-box;
    z-index: 9999;
    resize: none;
    line-height: 1.4;
  `;
  document.body.appendChild(textarea);

  // Placeholder rectangle behind the textarea
  const taBg = scene.add.rectangle(cx, cy - boxH / 2 + 38 + taH / 2, taW, taH, 0x111133, 0.8)
    .setStrokeStyle(1, 0x4466aa, 0.6);
  container.add(taBg);

  // Disable Phaser keyboard while textarea is focused
  const phaserKb = scene.input.keyboard;
  if (phaserKb) phaserKb.enabled = false;

  const dismiss = () => {
    textarea.remove();
    if (phaserKb) phaserKb.enabled = true;
    container.destroy();
  };

  const doConfirm = () => {
    const val = textarea.value.trim();
    if (val) {
      SoundManager.play('clickConfirm');
      dismiss();
      onConfirm(val);
    }
  };

  // Ctrl+Enter to confirm, Escape to cancel (Enter alone = newline)
  textarea.addEventListener('keydown', (e: KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); doConfirm(); }
    if (e.key === 'Escape') { SoundManager.play('clickCancel'); dismiss(); }
  });

  // Confirm button
  const btnY = cy + boxH / 2 - 26;
  const confirmBtn = createTechButton(scene, cx - 55, btnY, confirmLabel, {
    onClick: doConfirm,
    width: 90, height: 26, fontSize: '11px', color: TECH.SUCCESS, depth: 201,
  });
  container.add(confirmBtn);

  // Cancel button
  const cancelBtn = createTechButton(scene, cx + 55, btnY, cancelLabel, {
    onClick: () => { SoundManager.play('clickCancel'); dismiss(); },
    width: 90, height: 26, fontSize: '11px', color: TECH.DIM, depth: 201,
  });
  container.add(cancelBtn);

  // Click overlay to cancel
  overlay.on('pointerdown', () => { SoundManager.play('clickCancel'); dismiss(); });

  // Focus textarea on next frame
  scene.time.delayedCall(50, () => {
    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
  });

  return container;
}

// ══════════════════════════════════════════
//  SELECTION POPUP
// ══════════════════════════════════════════

export interface SelectionItem<T extends string> {
  value: T;
  label: string;
  description: string;
  disabled?: boolean;
  disabledReason?: string;
}

/**
 * Show a modal selection popup with a scrollable list of items.
 * Each item shows a label + description. Disabled items are dimmed but still selectable.
 * Returns the container (auto-destroys on selection or click-outside).
 */
export function showSelectionPopup<T extends string>(
  scene: Phaser.Scene,
  title: string,
  items: SelectionItem<T>[],
  onSelect: (value: T) => void,
  currentValue?: T,
): Phaser.GameObjects.Container {
  const container = scene.add.container(0, 0).setDepth(200);
  const dismiss = () => { container.destroy(); };

  // Fullscreen dim overlay (click-outside to dismiss)
  const overlay = scene.add.rectangle(
    CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2,
    CANVAS_WIDTH, CANVAS_HEIGHT,
    0x000000, 0.5,
  ).setInteractive();
  overlay.on('pointerdown', () => { SoundManager.play('clickCancel'); dismiss(); });
  container.add(overlay);

  // Popup dimensions
  const popW = 340;
  const rowH = 36;
  const titleH = 28;
  const maxVisibleRows = 7;
  const contentH = Math.min(items.length, maxVisibleRows) * rowH;
  const popH = titleH + contentH + 8;

  const cx = CANVAS_WIDTH / 2;
  const cy = CANVAS_HEIGHT / 2;
  const popX = cx - popW / 2;
  const popY = cy - popH / 2;

  // Background
  const bg = scene.add.rectangle(cx, cy, popW, popH, 0x0a0a1e, 0.97)
    .setStrokeStyle(1.5, TECH.COLOR, 0.6);
  container.add(bg);

  // Title
  const titleText = scene.add.text(cx, popY + 14, title, {
    fontSize: '11px', color: hexStr(TECH.ACCENT), fontFamily: 'monospace', fontStyle: 'bold',
  }).setOrigin(0.5);
  container.add(titleText);

  // Scrollable content area
  const listContainer = scene.add.container(0, 0);
  container.add(listContainer);

  // Build rows
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const ry = popY + titleH + i * rowH;
    const isCurrent = item.value === currentValue;

    // Row background (hover area)
    const rowBg = scene.add.rectangle(cx, ry + rowH / 2, popW - 8, rowH - 2, 0x111133, 0)
      .setInteractive({ useHandCursor: true });
    listContainer.add(rowBg);

    // Label
    const labelColor = item.disabled ? '#666688' : isCurrent ? '#ffcc00' : '#ffffff';
    const labelText = scene.add.text(popX + 12, ry + 4, item.label, {
      fontSize: '10px', color: labelColor, fontFamily: 'monospace', fontStyle: 'bold',
    });
    listContainer.add(labelText);

    // Description
    const descColor = item.disabled ? '#444466' : '#888899';
    const descText = scene.add.text(popX + 12, ry + 18, item.description, {
      fontSize: '8px', color: descColor, fontFamily: 'monospace',
      wordWrap: { width: popW - 24 },
    });
    listContainer.add(descText);

    // Warning reason for disabled items
    if (item.disabled && item.disabledReason) {
      const warnText = scene.add.text(popX + popW - 12, ry + 10, item.disabledReason, {
        fontSize: '8px', color: '#cc8844', fontFamily: 'monospace',
      }).setOrigin(1, 0.5);
      listContainer.add(warnText);
    }

    // Current value marker
    if (isCurrent) {
      const marker = scene.add.text(popX + popW - 12, ry + 4, '●', {
        fontSize: '10px', color: '#ffcc00', fontFamily: 'monospace',
      }).setOrigin(1, 0);
      listContainer.add(marker);
    }

    // Hover effect
    rowBg.on('pointerover', () => rowBg.setFillStyle(0x222255, 0.8));
    rowBg.on('pointerout', () => rowBg.setFillStyle(0x111133, 0));

    // Select
    rowBg.on('pointerdown', () => {
      SoundManager.play('clickConfirm');
      dismiss();
      onSelect(item.value);
    });
  }

  // Scrolling if items exceed visible area
  if (items.length > maxVisibleRows) {
    const maskY = popY + titleH;
    const maskH = contentH;
    const maskGfx = scene.make.graphics({ x: 0, y: 0 }, false);
    maskGfx.fillRect(popX, maskY, popW, maskH);
    listContainer.setMask(maskGfx.createGeometryMask());

    let scrollOffset = 0;
    const maxScroll = (items.length - maxVisibleRows) * rowH;

    scene.input.on('wheel', (_p: Phaser.Input.Pointer, _gos: unknown, _dx: number, dy: number) => {
      scrollOffset = Phaser.Math.Clamp(scrollOffset + dy * 0.5, 0, maxScroll);
      listContainer.y = -scrollOffset;
    });
  }

  return container;
}
